import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageLayout from '../../ui/layout/PageLayout';
import useManualEditor from './hooks/useManualEditor';
import { FontPicker } from '../fonts';
import { MediaPicker } from '../media';
import Tooltip from '../../ui/Tooltip/Tooltip';
import './styles.css';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ManualCreatePage() {
  const navigate = useNavigate();
  const query = useQuery();
  const designId = query.get('designId');
  const initialImageUrl = query.get('imageUrl');

  const {
    canvasElementRef,
    isReady,
    isSaving,
    isLoading,
    error,
    layers,
    activeLayerId,
    textStyle,
    applyTextStyle,
    selectedPhotoMedia,
    setSelectedPhotoMedia,
    selectedVideoMedia,
    setSelectedVideoMedia,
    videoSourceUrl,
    addText,
    addImageFromUrl,
    addVideoFromUrl,
    uploadImage,
    addMarketingLogo,
    addDefaultLogo,
    bringForward,
    sendBackward,
    rotateSelected,
    removeSelected,
    selectLayer,
    saveCanvas,
    exportPng,
    downloadVideo,
  } = useManualEditor({ initialDesignId: designId, initialImageUrl });

  return (
    <PageLayout
      title="Criação Manual"
      subtitle="Editor visual para montar artes com camadas, imagens e exportação PNG"
      secondary={null}
      primary={(
        <>
          <div className="manual-footer">
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/contents')}>
              Ir para Conteúdos
            </button>
            <button className="btn btn-secondary" type="button" onClick={saveCanvas} disabled={!isReady || isSaving}>
              {isSaving ? 'Salvando...' : 'Salvar JSON'}
            </button>
            <Tooltip content="Baixe sua arte finalizada em alta qualidade (PNG)." variant="tip">
              <button className="btn btn-primary" type="button" onClick={exportPng} disabled={!isReady || Boolean(videoSourceUrl)}>
                Exportar PNG
              </button>
            </Tooltip>
            <button className="btn btn-secondary" type="button" onClick={downloadVideo} disabled={!videoSourceUrl}>
              Baixar Vídeo Original
            </button>
          </div>

          <div className="manual-editor">
            <aside className="manual-panel">
              <h3>Elementos</h3>
              <div className="manual-actions">
                <Tooltip content="Insira um novo bloco de texto para editar título, mensagem ou CTA." variant="info" block>
                  <button className="btn btn-secondary" type="button" onClick={addText}>Adicionar texto</button>
                </Tooltip>
                <button className="btn btn-secondary" type="button" onClick={removeSelected}>Excluir camada</button>
              </div>

              <div className="manual-row">
                <button className="btn btn-secondary" type="button" onClick={bringForward}>Trazer frente</button>
                <button className="btn btn-secondary" type="button" onClick={sendBackward}>Enviar trás</button>
              </div>

              <div className="manual-row">
                <button className="btn btn-secondary" type="button" onClick={() => rotateSelected(15)}>Girar +15°</button>
                <button className="btn btn-secondary" type="button" onClick={() => rotateSelected(-15)}>Girar -15°</button>
              </div>

              <h3>Texto</h3>
              <div className="manual-text-controls">
                <Tooltip content="Pesquise e aplique fontes diretamente do Google Fonts." variant="tip" block>
                  <FontPicker
                    label="Fonte"
                    value={{ family: textStyle.family, weight: textStyle.weight, preview_url: textStyle.preview_url }}
                    onChange={(font) => applyTextStyle({ family: font.family, weight: font.weight, preview_url: font.preview_url })}
                  />
                </Tooltip>
                <label>
                  Peso
                  <input
                    type="number"
                    min="100"
                    max="900"
                    step="100"
                    value={textStyle.weight}
                    onChange={(event) => applyTextStyle({ weight: event.target.value })}
                  />
                </label>
                <label>
                  Tamanho
                  <input
                    type="number"
                    min="8"
                    max="300"
                    value={textStyle.size}
                    onChange={(event) => applyTextStyle({ size: Number(event.target.value) })}
                  />
                </label>
                <label>
                  Espaçamento
                  <input
                    type="number"
                    min="-200"
                    max="800"
                    value={textStyle.spacing}
                    onChange={(event) => applyTextStyle({ spacing: Number(event.target.value) })}
                  />
                </label>
              </div>

              <div className="manual-row">
                <button className="btn btn-secondary" type="button" onClick={addMarketingLogo}>Logo colorida</button>
                <button className="btn btn-secondary" type="button" onClick={addDefaultLogo}>Logo branca</button>
              </div>

              <h3>Imagem</h3>
              <Tooltip content="Pesquise imagens e vídeos profissionais para usar na sua criação." variant="tip" block>
                <MediaPicker
                  type="photo"
                  value={selectedPhotoMedia}
                  onChange={(item) => {
                    setSelectedPhotoMedia(item);
                    addImageFromUrl(item.large || item.original || item.medium || item.small || item.thumb);
                  }}
                />
              </Tooltip>

              <h3>Vídeo</h3>
              <MediaPicker
                type="video"
                value={selectedVideoMedia}
                onChange={(item) => {
                  setSelectedVideoMedia(item);
                  addVideoFromUrl(item.media_url || item.video_files?.[0]?.link || '');
                }}
              />

              <input
                className="manual-upload"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(event) => uploadImage(event.target.files?.[0])}
              />

              <Tooltip content="Gerencie a ordem das camadas para controlar sobreposição de texto, imagem e elementos." variant="info" block>
                <div>
                  <h3>Camadas</h3>
                  <ul className="manual-layers">
                    {layers.map((layer) => (
                      <li key={layer.id}>
                        <button
                          type="button"
                          className={layer.id === activeLayerId ? 'is-active' : ''}
                          onClick={() => selectLayer(layer.id)}
                        >
                          {layer.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </Tooltip>
            </aside>

            <main className="manual-canvas-wrap">
              <Tooltip content="Área onde você pode editar livremente sua arte." variant="info" block>
                <div className="manual-canvas">
                  <canvas ref={canvasElementRef} />
                </div>
              </Tooltip>
            </main>

            <aside className="manual-panel">
              <h3>Imagem Selecionada</h3>
              <p>Ao aplicar, a imagem é inserida no canvas com manipulação completa.</p>
              {selectedPhotoMedia && (
                <div className="manual-selected-image">
                  <img src={selectedPhotoMedia.small || selectedPhotoMedia.thumb || selectedPhotoMedia.medium || selectedPhotoMedia.large} alt={selectedPhotoMedia.author || 'Imagem selecionada'} />
                  <small>
                    Foto por {selectedPhotoMedia.author || 'Pexels'} · <a href={selectedPhotoMedia.author_url || 'https://www.pexels.com'} target="_blank" rel="noreferrer">Pexels</a>
                  </small>
                </div>
              )}

              {(isLoading || error) && (
                <p className="manual-error">
                  {isLoading ? 'Carregando design...' : error}
                </p>
              )}
            </aside>
          </div>
        </>
      )}
    />
  );
}
